/**
 * Full-page invoice details: summary and single table of appointments with inline adjustment indicators.
 * Replaces the previous modal; reached via Finances or Practice Finances invoice row click.
 */

import {
  Badge,
  Button,
  Container,
  Group,
  Paper,
  ScrollArea,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconArrowLeft, IconDownload } from '@tabler/icons-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

const FINANCES_TAB_INVOICE = 'invoice';
import {
  formatCurrency,
  MOCK_INVOICES,
  type Invoice,
  type SessionLineItem,
} from './FinancesPage';

function mergeAndSortLineItems(sessions: SessionLineItem[], adjustments: SessionLineItem[]): SessionLineItem[] {
  const combined = [...sessions, ...adjustments];
  return combined.sort((a, b) => {
    const dateCmp = a.date.localeCompare(b.date);
    return dateCmp !== 0 ? dateCmp : (a.time || '').localeCompare(b.time || '');
  });
}

function tableColumns() {
  return (
    <>
      <Table.Th>Date</Table.Th>
      <Table.Th>Time</Table.Th>
      <Table.Th>Client</Table.Th>
      <Table.Th>Provider</Table.Th>
      <Table.Th>CPT Code</Table.Th>
      <Table.Th>Duration</Table.Th>
      <Table.Th>Rate Type</Table.Th>
      <Table.Th>Status</Table.Th>
      <Table.Th>Amount Paid</Table.Th>
    </>
  );
}

function SessionsTable({ rows }: { rows: SessionLineItem[] }) {
  return (
    <ScrollArea h={420}>
      <Table highlightOnHover>
        <Table.Thead>
          <Table.Tr>{tableColumns()}</Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((s) => {
            const isAdjusted = s.reason != null && s.reason !== '';
            return (
              <Table.Tr key={s.id}>
                <Table.Td><Text size="sm">{s.date}</Text></Table.Td>
                <Table.Td><Text size="sm">{s.time}</Text></Table.Td>
                <Table.Td><Text size="sm">{s.client}</Text></Table.Td>
                <Table.Td><Text size="sm">{s.provider}</Text></Table.Td>
                <Table.Td><Text size="sm" ff="monospace">{s.cptCode}</Text></Table.Td>
                <Table.Td><Text size="sm">{s.duration} min</Text></Table.Td>
                <Table.Td><Text size="sm">{s.rateType}</Text></Table.Td>
                <Table.Td>
                  {isAdjusted ? (
                    <Tooltip label={s.reason}>
                      <Badge size="sm" variant="light" color="orange">Adjusted</Badge>
                    </Tooltip>
                  ) : (
                    <Text size="sm" c="gray.5">—</Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c={s.amount < 0 ? 'red.6' : undefined}>{formatCurrency(s.amount)}</Text>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </ScrollArea>
  );
}

export function InvoiceDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.replace(/\/invoice\/[^/]+$/, '');
  const backUrl = `${basePath}?tab=${FINANCES_TAB_INVOICE}`;
  const invoice: Invoice | undefined = id ? MOCK_INVOICES.find((inv) => inv.id === id) : undefined;

  if (!invoice) {
    return (
      <Container size="xl" py="xl">
        <Stack gap="lg">
          <Button variant="subtle" leftSection={<IconArrowLeft size={18} />} onClick={() => navigate(backUrl)}>
            Back to invoices
          </Button>
          <Text c="dimmed">Invoice not found.</Text>
        </Stack>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Group justify="space-between" align="flex-start">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={18} />}
            onClick={() => navigate(backUrl)}
          >
            Back to invoices
          </Button>
          <Button leftSection={<IconDownload size={18} />}>
            Download as PDF
          </Button>
        </Group>

        <Paper withBorder p="lg" radius="md">
          <Title order={2} mb="xs">Invoice Details</Title>
          <Text size="sm" c="gray.6" fw={600} mb="md">Pay period: {invoice.payPeriod}</Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="lg">
            <div>
              <Text size="xs" c="gray.6" fw={600}>Amount</Text>
              <Text fw={600}>{formatCurrency(invoice.amount)}</Text>
            </div>
            <div>
              <Text size="xs" c="gray.6" fw={600}>Generation Date</Text>
              <Text size="sm">{invoice.date}</Text>
            </div>
            <div>
              <Text size="xs" c="gray.6" fw={600}>Bank Account</Text>
              <Text size="sm">{invoice.bankAccount}</Text>
            </div>
            <div>
              <Text size="xs" c="gray.6" fw={600}>Status</Text>
              <Badge
                size="sm"
                variant="light"
                color={invoice.status === 'Paid' ? 'green' : invoice.status === 'Pending' ? 'yellow' : 'red'}
              >
                {invoice.status}
              </Badge>
            </div>
            <div>
              <Text size="xs" c="gray.6" fw={600}>Stripe ID</Text>
              <Text size="sm" ff="monospace">{invoice.stripeId}</Text>
            </div>
          </SimpleGrid>

          <Text fw={600} mb="md">
            Appointments ({invoice.sessions.length + invoice.adjustments.length})
          </Text>
          <SessionsTable rows={mergeAndSortLineItems(invoice.sessions, invoice.adjustments)} />
        </Paper>
      </Stack>
    </Container>
  );
}
